"use client";

import { useEffect } from "react";
import { useGetPublicLegalSettingsQuery } from "@/store";
import { Shield } from "lucide-react";
import { LegalPageLayout } from "@/components/LegalPageLayout";
import { DEFAULT_PRIVACY_POLICY } from "@/constants/legalContent";
import { SEO } from "@/components/seo";

export function PrivacyPolicyScreen() {
  const { data: legalSettings, isLoading } = useGetPublicLegalSettingsQuery();

  useEffect(() => {
    // Set page title
    document.title = "Privacy Policy - Zest and Zeal";
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="bg-card/50 backdrop-blur-sm rounded-2xl border border-border/50 shadow-xl shadow-black/5 overflow-hidden">
              <div className="p-8 md:p-12">
                <div className="animate-pulse space-y-6">
                  <div className="h-8 bg-muted rounded-lg w-3/4"></div>
                  <div className="h-4 bg-muted rounded w-full"></div>
                  <div className="h-4 bg-muted rounded w-5/6"></div>
                  <div className="h-4 bg-muted rounded w-4/5"></div>
                  <div className="h-6 bg-muted rounded w-1/2 mt-8"></div>
                  <div className="h-4 bg-muted rounded w-full"></div>
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const content = legalSettings?.data?.privacyPolicy || DEFAULT_PRIVACY_POLICY;

  return (
    <LegalPageLayout
      title="Privacy Policy"
      icon={<Shield className="h-7 w-7 text-blue-500" />}
      lastUpdated={new Date()}
    >
      <SEO 
        title="Privacy Policy"
        description="Our privacy policy explains how we collect and use your data to provide our food delivery services."
        canonical="/privacy-policy"
      />
      <div className="relative">
        <div
          className="prose prose-lg prose-gray dark:prose-invert max-w-none
                     prose-headings:font-semibold prose-headings:text-foreground
                     prose-h1:text-3xl prose-h1:font-bold prose-h1:mb-6 prose-h1:mt-0
                     prose-h2:text-2xl prose-h2:font-semibold prose-h2:mt-8 prose-h2:mb-4 prose-h2:text-blue-600 dark:prose-h2:text-blue-400
                     prose-h3:text-xl prose-h3:font-medium prose-h3:mt-6 prose-h3:mb-3
                     prose-p:text-foreground prose-p:leading-relaxed prose-p:mb-4
                     prose-li:text-foreground prose-li:leading-relaxed prose-li:mb-2
                     prose-ul:my-4 prose-ul:space-y-2 prose-ul:ml-6
                     prose-ol:my-6 prose-ol:space-y-4
                     prose-strong:text-foreground prose-strong:font-semibold
                     prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                     prose-blockquote:border-l-primary prose-blockquote:text-muted-foreground prose-blockquote:bg-muted/30 prose-blockquote:p-4 prose-blockquote:rounded-lg
                     prose-hr:border-border prose-hr:my-8"
          dangerouslySetInnerHTML={{ __html: content }}
        />

        {/* Custom CSS for policy list styling */}
        <style dangerouslySetInnerHTML={{
          __html: `
            .policy-list {
              counter-reset: section;
              margin: 1.5rem 0 !important;
              padding: 0 !important;
            }
            .policy-list > li {
              counter-increment: section;
              margin-bottom: 2rem !important;
              padding-left: 2rem !important;
              position: relative !important;
              list-style: none !important;
            }
            .policy-list > li::before {
              content: counter(section) ". ";
              position: absolute;
              left: 0;
              top: 0;
              font-weight: 600;
              color: rgb(37 99 235);
              font-size: 1.125rem;
            }
            .dark .policy-list > li::before {
              color: rgb(96 165 250);
            }
            .policy-list > li > h2 {
              margin-top: 0 !important;
              margin-bottom: 1rem !important;
              padding-left: 0 !important;
            }
            .policy-list > li > ul {
              margin-top: 0.5rem !important;
              margin-left: 1.5rem !important;
            }
            .policy-list > li > ul > li {
              list-style-type: disc !important;
              margin-bottom: 0.5rem !important;
            }
            .policy-list > li > p:first-of-type {
              margin-top: 0.5rem !important;
            }
          `
        }} />
      </div>
    </LegalPageLayout>
  );
}